import { useCallback, useMemo, useState } from 'react';

import { ConstructorApi } from '../api';
import { useProducts } from './useProducts';
import { queryClient } from '@/app/QueryClientProvider';
import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { usePopups } from '@/hooks';

export const useProduct = (id) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const { data, refetch } = useProducts();

  const productData = useMemo(() => {
    if (!data || !data?.length) return undefined;

    return data.find((item) => item.id === id);
  }, [id, data]);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleCreate = useCallback(async (product) => {
    try {
      setIsCreating(true);
      const result = await ConstructorApi.createProduct(product);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: product.id })
        });
        showSuccessToast('Created');
        return result;
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsCreating(false);
    }
  }, []);

  const handleUpdate = useCallback(async (product) => {
    try {
      setIsUpdating(true);
      const result = await ConstructorApi.updateProduct(product);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: product.id })
        });
        showSuccessToast('Saved');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (product) => {
    try {
      setIsDeleting(true);
      const result = await ConstructorApi.deleteProduct(product);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllProducts]
        });
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const handleFileUpload = useCallback(async ({ file, productId, name }) => {
    try {
      setIsUploading(true);
      const result = await ConstructorApi.addFileToProduct({ file, productId, name });
      if (result) {
        await refetch();
        showSuccessToast('Uploaded');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleFileDelete = useCallback(async (productFileToDownloadId) => {
    try {
      setIsDeleting(true);
      const result = await ConstructorApi.deleteFileFromProduct(productFileToDownloadId);
      if (result) {
        await refetch();
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    data: productData,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleFileUpload,
    handleFileDelete,
    isCreating,
    isUpdating,
    isDeleting,
    isUploading,
    refetch
  };
};
